import { CloseIcon, AlertOIcon } from '@floriday/floriday-icons'
import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Stack,
  Typography,
  DialogActions,
  Button,
} from '@mui/material'
import { RfhColors } from '../../config/florimixColors'
import { useState } from 'react'
import { SpinnerButton } from '@floriday/floriday-ui'
import { useTranslation } from 'react-i18next'

type TransactionDeleteDialogProps = {
  isOpen: boolean
  onClose: () => void
  transactionId: string | null
  onDelete: (transactionId: string) => Promise<void>
}

export const TransactionDeleteDialog = ({
  isOpen,
  onClose,
  transactionId,
  onDelete,
}: TransactionDeleteDialogProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const handleClose = () => {
    onClose()
  }

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClose()
  }

  const handleDelete = async () => {
    if (transactionId) {
      setIsLoading(true)
      try {
        await onDelete(transactionId)
        onClose()
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle sx={{ fontSize: 16 }}>
        {t('transactions.deleteDialog.title')}
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleCloseClick}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Stack
          direction='column'
          sx={{
            gap: 1,
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <IconButton>
            <AlertOIcon sx={{ color: RfhColors.alertRed }} />
          </IconButton>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
            }}
          >
            {t('transactions.deleteDialog.heading')}
          </Typography>
          <Typography variant='body1' sx={{ color: RfhColors.darkGrey }}>
            {t('transactions.deleteDialog.description')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: RfhColors.lightGrey, padding: 2 }}>
        <Button
          sx={{
            border: `1px solid #BCBBBF`,
            backgroundColor: RfhColors.white,
          }}
          onClick={handleCloseClick}
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <SpinnerButton
          variant='contained'
          sx={{ color: RfhColors.white, backgroundColor: RfhColors.alertRed }}
          onClick={handleDelete}
          disabled={isLoading}
          showSpinner={isLoading}
        >
          {t('transactions.deleteDialog.confirm')}
        </SpinnerButton>
      </DialogActions>
    </Dialog>
  )
}
